import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink, RouterOutlet } from '@angular/router';
import type { GzSelectOption } from '@gozem/ui';
import { TranslationService } from '../../core/services/translation.service';
import type { Locale } from '../../../assets/i18n/translations';

interface TrustItem {
  icon: string;
  label: string;
}

@Component({
  selector: 'app-auth-layout',
  imports: [RouterOutlet, RouterLink],
  templateUrl: './auth-layout.html',
  styleUrl: './auth-layout.css',
})
export class AuthLayout {
  private readonly translationService = inject(TranslationService);

  readonly t = computed(() => this.translationService.translations());
  readonly locale = computed(() => this.translationService.locale());

  /**
   * Navbar scroll state. The auth shell scrolls its own `.auth` container (not
   * the window), so — unlike the landing header, which reads `window.scrollY`
   * — we read the container's scrollTop from its scroll event. Drives the
   * transparent → dark-glass transition on `.auth__nav`.
   */
  readonly scrolled = signal<boolean>(false);
  onAuthScroll(event: Event): void {
    this.scrolled.set((event.target as HTMLElement).scrollTop > 24);
  }

  /**
   * Reset the shell on each child-route change. The layout persists across
   * login → select-account (only the outlet swaps), so without this the scroll
   * position and the `scrolled` glass state would carry over: the new page
   * could open part-scrolled with the header stuck in its glass state. Snap
   * back to the top with the transparent header so every auth page starts clean.
   */
  onRouteActivate(scrollHost: HTMLElement): void {
    scrollHost.scrollTop = 0;
    this.scrolled.set(false);
  }

  /** Language options for the navbar switcher (flag folded into the label). */
  readonly languageOptions: GzSelectOption<Locale>[] = [
    { value: 'fr', label: '🇫🇷 Français' },
    { value: 'en', label: '🇬🇧 English' },
  ];

  /** Language toggle aria-label (no dedicated i18n key needed). */
  readonly langLabel = computed(() => (this.locale() === 'fr' ? 'Langue' : 'Language'));

  /**
   * Hero badge + trust chips. Kept local to the layout so we don't have to
   * extend the shared translations schema for decorative marketing copy.
   */
  readonly heroExtras = computed<{ badge: string; trust: TrustItem[] }>(() => {
    const fr = this.locale() === 'fr';
    return {
      badge: fr ? 'La Super App N°1 en Afrique' : "Africa's N°1 Super App",
      trust: fr
        ? [
            { icon: 'ri-global-line', label: '5 pays' },
            { icon: 'ri-team-line', label: '+12 000 partenaires' },
          ]
        : [
            { icon: 'ri-global-line', label: '5 countries' },
            { icon: 'ri-team-line', label: '+12,000 partners' },
          ],
    };
  });

  setLocale(value: Locale): void {
    this.translationService.setLocale(value);
  }
}
